import React, { useState, useEffect } from 'react';
import apiFetch from '../../../../utils/api';
import { useAuth } from '../../../../context/AuthContext';
import { useStats } from '../../../../context/StatsContext';
import Preloader from '../../../../components/Preloader/Preloader';
import './Profile.css';

const Profile = () => {
  const { user, login } = useAuth();
  const { stats, loadingStats } = useStats();
  
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  
  // Display Data
  const [userData, setUserData] = useState({
    paymentMethod: 'Not provided',
    accountNumber: 'Not provided',
    accountTitle: 'Not provided'
  });

  // Forms State
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  useEffect(() => {
    if (user) {
      apiFetch('/api/me')
        .then(res => res.json())
        .then(data => {
          if (data.account) {
            setUserData({
              paymentMethod: data.account.paymentMethod || 'Not provided',
              accountNumber: data.account.accountNumber || 'Not provided',
              accountTitle: data.account.accountTitle || 'Not provided',
            });
          }
        })
        .catch(() => {});
    }
  }, [user]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMsg({ text: '', type: '' });

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      return setMsg({ text: 'New passwords do not match.', type: 'error' });
    }
    if (passwordForm.newPassword.length < 8) {
      return setMsg({ text: 'Password must be at least 8 characters.', type: 'error' });
    }

    setLoading(true);
    try {
      const res = await apiFetch('/api/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(passwordForm),
      });
      const data = await res.json();
      if (data.success) {
        setMsg({ text: 'Password changed successfully!', type: 'success' });
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        if (data.token) login(data.token, user);
      } else {
        setMsg({ text: data.message || 'Password change failed.', type: 'error' });
      }
    } catch {
      setMsg({ text: 'Server error. Try again.', type: 'error' });
    }
    setLoading(false);
  };

  if (loadingStats) return <Preloader message="Loading your profile..." />;

  const initials = (user?.username || 'U').slice(0, 2).toUpperCase();

  return (
    <div className="prof-page">
      <div className="prof-header">
        <div className="prof-header__content">
          <div className="prof-avatar-large">{initials}</div>
          <div className="prof-header__text">
            <h1 className="prof-header__name">Welcome back, {user?.username}</h1>
            <p className="prof-header__meta">Manage your account settings and security</p>
          </div>
        </div>
        <div className="prof-header__stats">
          <div className="prof-header__stat">
            <span>{stats?.team.totalTeamSize || 0}</span>
            <label>Team Size</label>
          </div>
          <div className="prof-header__stat">
            <span>Rs. {stats?.availableBalance.toLocaleString() || 0}</span>
            <label>Balance</label>
          </div>
        </div>
      </div>

      <div className="prof-container" style={{ gridTemplateColumns: '1fr' }}>
        <div className="prof-content">

          {msg.text && (
            <div className={`prof-alert prof-alert--${msg.type}`}>
              {msg.type === 'error' ? '❌' : '✅'} {msg.text}
            </div>
          )}

          <div className="prof-card" style={{ marginBottom: '2rem' }}>
            <h3 className="prof-form__title">Profile Details</h3>
            
            <div className="prof-details-grid">
              <div className="prof-detail-item">
                <label>Username</label>
                <div className="prof-detail-value">{user?.username}</div>
              </div>
              <div className="prof-detail-item">
                <label>Email Address</label>
                <div className="prof-detail-value">{user?.email || 'Not provided'}</div>
              </div>
              <div className="prof-detail-item">
                <label>Payment Method</label>
                <div className="prof-detail-value">{userData.paymentMethod}</div>
              </div>
              <div className="prof-detail-item">
                <label>Account Number</label>
                <div className="prof-detail-value">{userData.accountNumber}</div>
              </div>
              <div className="prof-detail-item">
                <label>Account Title</label>
                <div className="prof-detail-value">{userData.accountTitle}</div>
              </div>
            </div>
          </div>

          <div className="prof-card">
            <form onSubmit={handleChangePassword} className="prof-form">
              <h3 className="prof-form__title">Change Password</h3>
              <div className="prof-form__group">
                <label>Current Password</label>
                <input 
                  type="password" 
                  value={passwordForm.currentPassword}
                  onChange={e => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                  required
                />
              </div>
              <div className="prof-form__group">
                <label>New Password</label>
                <input 
                  type="password" 
                  value={passwordForm.newPassword}
                  onChange={e => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                  required
                />
              </div>
              <div className="prof-form__group">
                <label>Confirm New Password</label>
                <input 
                  type="password" 
                  value={passwordForm.confirmPassword}
                  onChange={e => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  required
                />
              </div>
              <button type="submit" className="prof-btn-save" disabled={loading}>
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Profile;
